import { ArrowRight, Shield } from 'lucide-react';
import kvImage from '@/assets/promos/kv_WAAP_Tachnen.png';
import { WAF_GUIDE_URL } from './WafPromoBanner';
import styles from './WaapOnboardingHero.module.css';

export interface WaapOnboardingStep {
  label: string;
  /** Short sublabel shown under the step name */
  meta: string;
  done: boolean;
}

export interface WaapOnboardingHeroProps {
  steps: WaapOnboardingStep[];
  onActivate: () => void;
  activating: boolean;
  /** Primary CTA label — differs between first activation and resuming setup. */
  ctaLabel?: string;
}

const STEP_LABELS = [
  { name: 'Create organization', meta: (s: WaapOnboardingStep) => s.meta },
  { name: 'Domain & origin',     meta: () => '~2 min' },
  { name: 'CertInvent',          meta: () => 'Activate SSL certificates' },
  { name: 'AI-WAF rules',        meta: () => 'Enable a protection policy' },
] as const;

/** Checkmark SVG used inside the done badge */
function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12.5 4.5 4.5L19 7" />
    </svg>
  );
}

/** Arrow separator between steps */
function StepArrow() {
  return (
    <div className={styles.stepArrow}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12h15M13 6l6 6-6 6" />
      </svg>
    </div>
  );
}

interface StepChipProps {
  step: WaapOnboardingStep;
  index: number;
  isNext: boolean;
  chipCls: string;
  isLast: boolean;
}

function StepChip({ step, index, isNext, chipCls, isLast }: StepChipProps) {
  const statusLabelCls = [
    styles.stepStatusLabel,
    step.done ? styles.stepStatusLabelDone
      : isNext ? styles.stepStatusLabelNext
      : styles.stepStatusLabelWaiting,
  ].join(' ');

  const metaCls = [
    styles.stepMeta,
    step.done ? styles.stepMetaDone
      : isNext ? styles.stepMetaNext
      : styles.stepMetaWaiting,
  ].join(' ');

  const metaText = step.done
    ? step.meta
    : STEP_LABELS[index].meta(step);

  return (
    <>
      <div className={chipCls}>
        <div className={styles.stepStatus}>
          {step.done ? (
            <span className={styles.stepBadgeDone} style={{ color: 'var(--vn-fg-on-solid)' }}>
              <CheckIcon />
            </span>
          ) : isNext ? (
            <span className={styles.stepBadgeNext}>{index + 1}</span>
          ) : (
            <span className={styles.stepBadgeWaiting}>{index + 1}</span>
          )}
          <span className={statusLabelCls}>
            {step.done ? 'Done' : isNext ? 'Next' : 'Waiting'}
          </span>
        </div>
        <div className={[styles.stepName, (!step.done && !isNext) ? styles.stepNameMuted : ''].filter(Boolean).join(' ')}>
          {STEP_LABELS[index].name}
        </div>
        <div className={metaCls}>{metaText}</div>
      </div>
      {!isLast && <StepArrow />}
    </>
  );
}

/**
 * Presentational WAAP onboarding hero — matches the "Portal Overview v2" mock.
 * Eligibility/dismiss logic lives in WafFreePromo; this component is visuals only.
 */
export function WaapOnboardingHero({
  steps,
  onActivate,
  activating,
  ctaLabel = 'Activate Web Application & API Protection',
}: WaapOnboardingHeroProps) {
  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;
  const progressPct = `${Math.round((completed / total) * 100)}%`;

  return (
    <div className={styles.root}>
      <div className={styles.hero}>
        <div className={styles.inner}>
          {/* ── Left: content ── */}
          <div className={styles.content}>
            {/* Eyebrow: badge + progress counter */}
            <div className={styles.eyebrow}>
              <span className={styles.badge}>Security · WAAP</span>
              <div className={styles.progress}>
                <div className={styles.progressCount}>
                  <span className={styles.progressDone}>{completed}</span>
                  <span className={styles.progressSep}>/</span>
                  <span className={styles.progressTotal}>{total}</span>
                  <span className={styles.progressLabel}>completed</span>
                </div>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: progressPct }} />
                </div>
              </div>
            </div>

            {/* Title */}
            <div className={styles.titleBlock}>
              <h2 className={styles.title}>
                Protect your site with <span className={styles.titleAccent}>WAAP</span>
              </h2>
              <div className={styles.subtitle}>Web Application &amp; API Protection</div>
              <p className={styles.desc}>
                Block OWASP Top-10, malicious bots and DDoS at the edge. Live in under 2 minutes.
              </p>
            </div>

            {/* 4-step chip row */}
            <div className={styles.steps}>
              {steps.map((step, i) => {
                const isNext = !step.done && steps.slice(0, i).every((s) => s.done);
                const chipCls = [
                  styles.step,
                  step.done ? styles.stepDone : '',
                  isNext ? styles.stepNext : '',
                ].filter(Boolean).join(' ');

                return (
                  <StepChip
                    key={STEP_LABELS[i].name}
                    step={step}
                    index={i}
                    isNext={isNext}
                    chipCls={chipCls}
                    isLast={i === steps.length - 1}
                  />
                );
              })}
            </div>

            {/* CTA row */}
            <div className={styles.ctaRow}>
              <button
                className={styles.ctaBtn}
                disabled={activating}
                onClick={onActivate}
              >
                <Shield size={15} />
                {activating ? 'Activating…' : ctaLabel}
                {!activating && <ArrowRight size={15} />}
              </button>
              <a
                className={styles.ctaBtnSecondary}
                href={WAF_GUIDE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Read the WAAP guide
              </a>
            </div>
          </div>

          {/* ── Right: KV image ── */}
          <div className={styles.kv}>
            <div className={styles.kvWash} />
            <img
              src={kvImage}
              alt="WAAP shield with WAF, bot management, DDoS protection, API protection and data security"
              className={styles.kvImg}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
